<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card stock-report-card">
          <div class="card-body">
            <div class="report-header-bar">
              <div>
                <h5>View Stock Report</h5>
                <p>Use View Report for screen data. Export creates Excel or PDF in the background and groups all-category downloads with category totals and a final stock subtotal.</p>
              </div>
              <div class="export-actions">
                <button
                  class="btn btn-outline-success"
                  @click="startExport('excel')"
                  :disabled="exportLoading"
                >
                  {{ exportLoading && exportJob.export_format === 'excel' ? 'Preparing...' : 'Export Excel' }}
                </button>
                <button
                  class="btn btn-outline-danger"
                  @click="startExport('pdf')"
                  :disabled="exportLoading"
                >
                  {{ exportLoading && exportJob.export_format === 'pdf' ? 'Preparing...' : 'Export PDF' }}
                </button>
              </div>
            </div>

            <div class="report-toolbar">
              <div class="filter-grid">
                <div class="form-group">
                  <label>Report Date</label>
                  <b-form-input v-model="filters.reportDate" type="date" />
                </div>
                <div class="form-group">
                  <label>Category</label>
                  <b-form-select v-model="filters.categoryid" :options="categoryOptions" />
                </div>
                <div class="form-group">
                  <label>View</label>
                  <b-form-select v-model="filters.reportMode" :options="reportModeOptions" />
                </div>
                <div class="form-group search-box">
                  <label>Search</label>
                  <b-form-input
                    v-model="filters.search"
                    type="search"
                    placeholder="Item code, item name, category..."
                    @input="onSearchInput"
                  />
                </div>
                <div class="button-group">
                  <button class="btn btn-submit" @click="applyFilters">
                    View Report
                  </button>
                  <button class="btn btn-outline-secondary" @click="resetFilters">
                    Reset
                  </button>
                </div>
              </div>
            </div>

            <div v-if="exportJob.job_id" class="export-status" :class="`status-${exportJob.status}`">
              <div class="export-status-copy">
                <strong>{{ exportStatusTitle }}</strong>
                <span>{{ exportStatusText }}</span>
              </div>
              <button
                v-if="exportJob.status === 'completed'"
                class="btn btn-sm btn-download"
                @click="downloadExport"
              >
                Download {{ exportJob.export_format === "pdf" ? "PDF" : "Excel" }}
              </button>
            </div>

            <div class="summary-strip">
              <div>
                <span>Total Items</span>
                <strong>{{ pagination.total }}</strong>
              </div>
              <div>
                <span>Total Qty</span>
                <strong>{{ formatNumber(pageTotals.quantity) }}</strong>
              </div>
              <div v-if="isValueMode">
                <span>Closing Value</span>
                <strong>{{ formatCurrency(pageTotals.closingstock) }}</strong>
              </div>
            </div>

            <div class="table-responsive">
              <a-table
                :columns="columns"
                :data-source="rows"
                :loading="loading"
                :pagination="paginationConfig"
                row-key="rowkey"
                @change="handleTableChange"
              >
                <template #bodyCell="{ column, record, index }">
                  <template v-if="column.key === 'slno'">
                    {{ serialNumber(index) }}
                  </template>
                  <template v-else-if="column.key === 'purchasevalue' || column.key === 'salevalue' || column.key === 'closingstock'">
                    <span class="money-text">{{ formatCurrency(record[column.key]) }}</span>
                  </template>
                  <template v-else-if="column.key === 'quantity'">
                    <span class="qty-text">{{ formatNumber(record.quantity) }}</span>
                  </template>
                  <template v-else-if="column.key === 'categoryname'">
                    <span class="category-pill">{{ record.categoryname }}</span>
                  </template>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const valueColumns = [
  { title: "Sl No", key: "slno", width: 70 },
  { title: "Category", dataIndex: "categoryname", key: "categoryname" },
  { title: "Item Code", dataIndex: "itemcode", key: "itemcode" },
  { title: "Item Desc", dataIndex: "itemdesc", key: "itemdesc" },
  { title: "Pur Value", dataIndex: "purchasevalue", key: "purchasevalue", align: "right" },
  { title: "Sale Value", dataIndex: "salevalue", key: "salevalue", align: "right" },
  { title: "Qty", dataIndex: "quantity", key: "quantity", align: "right" },
  { title: "U.O.M", dataIndex: "uom", key: "uom" },
  { title: "Closing Stock", dataIndex: "closingstock", key: "closingstock", align: "right" },
];

const quantityColumns = [
  { title: "Sl No", key: "slno", width: 70 },
  { title: "Category", dataIndex: "categoryname", key: "categoryname" },
  { title: "Item Code", dataIndex: "itemcode", key: "itemcode" },
  { title: "Item Desc", dataIndex: "itemdesc", key: "itemdesc" },
  { title: "Qty", dataIndex: "quantity", key: "quantity", align: "right" },
  { title: "U.O.M", dataIndex: "uom", key: "uom" },
];

export default {
  name: "inventoryreport",
  data() {
    return {
      title: "Stock Report",
      title1: "Category wise closing stock",
      rows: [],
      categories: [],
      loading: false,
      searchTimer: null,
      filters: {
        reportDate: this.today(),
        categoryid: "",
        reportMode: "value",
        search: "",
      },
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0,
      },
      exportJob: {
        job_id: "",
        status: "",
        export_format: "",
        filename: "",
        error: "",
      },
      exportPoller: null,
    };
  },
  computed: {
    categoryOptions() {
      return [
        { value: "", text: "All Categories" },
        ...this.categories.map((category) => ({
          value: category.categoryid,
          text: category.categoryname,
        })),
      ];
    },
    reportModeOptions() {
      return [
        { value: "value", text: "Value" },
        { value: "quantity", text: "Quantity Only" },
      ];
    },
    isValueMode() {
      return this.filters.reportMode === "value";
    },
    columns() {
      return this.isValueMode ? valueColumns : quantityColumns;
    },
    paginationConfig() {
      return {
        current: this.pagination.current,
        pageSize: this.pagination.pageSize,
        total: this.pagination.total,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      };
    },
    pageTotals() {
      return this.rows.reduce(
        (acc, row) => {
          acc.quantity += parseFloat(row.quantity) || 0;
          acc.closingstock += parseFloat(row.closingstock) || 0;
          return acc;
        },
        { quantity: 0, closingstock: 0 }
      );
    },
    exportLoading() {
      return ["queued", "running"].includes(this.exportJob.status);
    },
    exportStatusTitle() {
      if (this.exportJob.status === "completed") return "Export ready";
      if (this.exportJob.status === "failed") return "Export failed";
      if (this.exportLoading) return "Export is being generated";
      return "Export";
    },
    exportStatusText() {
      if (this.exportJob.status === "completed") {
        return this.exportJob.filename || "Your file is ready to download.";
      }
      if (this.exportJob.status === "failed") {
        return this.exportJob.error || "Something went wrong while generating the file.";
      }
      if (this.exportLoading) {
        return "Preparing grouped stock export with the selected filters.";
      }
      return "";
    },
  },
  created() {
    this.fetchCategories();
    this.fetchReport();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.clearExportPoller();
  },
  methods: {
    today() {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    },
    async fetchCategories() {
      try {
        const response = await axios.get("/v1/category");
        this.categories = response.data || [];
      } catch (error) {
        console.error("Failed to load categories", error);
      }
    },
    async fetchReport() {
      this.loading = true;
      try {
        const response = await axios.get("/v1/stockreport", {
          params: {
            report_date: this.filters.reportDate || undefined,
            categoryid: this.filters.categoryid || undefined,
            search: this.filters.search.trim() || undefined,
            report_mode: this.filters.reportMode,
            page: this.pagination.current,
            page_size: this.pagination.pageSize,
          },
        });
        this.rows = response.data.items || [];
        this.pagination = {
          current: response.data.page || this.pagination.current,
          pageSize: response.data.page_size || this.pagination.pageSize,
          total: response.data.total || 0,
        };
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to load stock report",
          text: error.response?.data?.detail || error.message,
        });
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.pagination.current = 1;
      this.fetchReport();
    },
    resetFilters() {
      this.filters = {
        reportDate: this.today(),
        categoryid: "",
        reportMode: "value",
        search: "",
      };
      this.pagination.current = 1;
      this.fetchReport();
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.applyFilters(), 350);
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.fetchReport();
    },
    clearExportPoller() {
      if (this.exportPoller) {
        window.clearInterval(this.exportPoller);
        this.exportPoller = null;
      }
    },
    async startExport(exportFormat) {
      this.clearExportPoller();
      try {
        const response = await axios.post("/v1/stockreport/export", null, {
          params: {
            report_date: this.filters.reportDate || undefined,
            categoryid: this.filters.categoryid || undefined,
            search: this.filters.search.trim() || undefined,
            report_mode: this.filters.reportMode,
            export_format: exportFormat,
          },
        });
        this.exportJob = {
          ...this.exportJob,
          ...response.data,
          error: "",
        };
        this.pollExportJob(this.exportJob.job_id);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to start export",
          text: error.response?.data?.detail || error.message,
        });
      }
    },
    async pollExportJob(jobId) {
      await this.fetchExportJob(jobId);
      this.exportPoller = window.setInterval(async () => {
        const finished = await this.fetchExportJob(jobId);
        if (finished) {
          this.clearExportPoller();
        }
      }, 2000);
    },
    async fetchExportJob(jobId) {
      try {
        const response = await axios.get(`/v1/stockreport/export/${jobId}`);
        this.exportJob = {
          ...this.exportJob,
          ...response.data,
        };
        return ["completed", "failed"].includes(this.exportJob.status);
      } catch (error) {
        this.clearExportPoller();
        this.exportJob = {
          ...this.exportJob,
          status: "failed",
          error: error.response?.data?.detail || error.message,
        };
        return true;
      }
    },
    async downloadExport() {
      if (!this.exportJob.job_id) return;
      try {
        const response = await axios.get(`/v1/stockreport/export/${this.exportJob.job_id}/download`, {
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: response.headers["content-type"] });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = this.exportJob.filename || `stock_report.${this.exportJob.export_format === "pdf" ? "pdf" : "xlsx"}`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Download failed",
          text: error.response?.data?.detail || error.message,
        });
      }
    },
    serialNumber(index) {
      return (this.pagination.current - 1) * this.pagination.pageSize + index + 1;
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.stock-report-card {
  border: none;
  box-shadow: 0 10px 30px rgba(31, 41, 55, 0.08);
}
.report-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 14px;
}
.report-header-bar h5 {
  margin: 0 0 4px;
  color: #0f172a;
}
.report-header-bar p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}
.export-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.report-toolbar {
  background: linear-gradient(135deg, #f8fbff 0%, #fff7ed 100%);
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 16px;
}
.filter-grid {
  display: grid;
  grid-template-columns: 180px 240px 180px minmax(240px, 1fr) max-content;
  gap: 12px;
  align-items: start;
}
.form-group label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 5px;
}
.button-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  align-self: start;
  padding-top: 22px;
}
.button-group .btn {
  width: auto;
  min-width: 86px;
  height: 44px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}
.button-group .btn-submit {
  min-width: 112px;
}
.export-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 16px;
  border: 1px solid #dbeafe;
  background: #f8fbff;
}
.export-status-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.export-status strong {
  color: #0f172a;
}
.export-status span {
  color: #64748b;
  font-size: 13px;
}
.status-completed {
  border-color: #bbf7d0;
  background: #f0fdf4;
}
.status-failed {
  border-color: #fecaca;
  background: #fef2f2;
}
.btn-download {
  background: #111827;
  color: #fff;
  border: none;
}
.summary-strip {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.summary-strip div {
  min-width: 160px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #111827;
  color: #fff;
}
.summary-strip span {
  display: block;
  font-size: 11px;
  opacity: 0.75;
}
.summary-strip strong {
  font-size: 18px;
}
.money-text,
.qty-text {
  font-family: "Courier New", monospace;
  font-weight: 700;
}
.category-pill {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 999px;
  background: #e8f2ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 700;
}
:deep(.ant-table-thead > tr > th),
:deep(.ant-table-tbody > tr > td) {
  vertical-align: top;
}
@media (max-width: 992px) {
  .report-header-bar {
    flex-direction: column;
  }
  .export-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .filter-grid {
    grid-template-columns: 1fr;
  }
  .button-group {
    padding-top: 0;
    justify-content: flex-start;
  }
  .export-status {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
